import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Card, CardContent } from '../ui/card';
import {
  Sparkles, CheckCircle2, AlertTriangle,
  FileText, Users, MapPin, Clock,
  Loader2, Check, X, RefreshCw, BookOpen, Wand2,
  Filter, Trash2, ChevronDown, ChevronRight, Play,
  Eye, ShieldCheck, Zap, Type, PenTool,
  Mic, BookText, Compass, TrendingUp, Tag, Download
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../lib/api';
import EditorTutorial, { TutorialTrigger } from './EditorTutorial';

const CATEGORY_CONFIG = {
  spelling:     { icon: Type,       label: 'Spelling',      color: 'text-blue-400',    bg: 'bg-blue-500/10' },
  punctuation:  { icon: PenTool,    label: 'Punctuation',   color: 'text-cyan-400',    bg: 'bg-cyan-500/10' },
  repetition:   { icon: RefreshCw,  label: 'Repetition',    color: 'text-amber-400',   bg: 'bg-amber-500/10' },
  character:    { icon: Users,      label: 'Character',     color: 'text-violet-400',  bg: 'bg-violet-500/10' },
  location:     { icon: MapPin,     label: 'Location',      color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  timeline:     { icon: Clock,      label: 'Timeline',      color: 'text-orange-400',  bg: 'bg-orange-500/10' },
  dialogue:     { icon: FileText,   label: 'Dialogue',      color: 'text-pink-400',    bg: 'bg-pink-500/10' },
  style:        { icon: Sparkles,   label: 'Style',         color: 'text-indigo-400',  bg: 'bg-indigo-500/10' },
  object:       { icon: BookOpen,   label: 'Object',        color: 'text-teal-400',    bg: 'bg-teal-500/10' },
  readability:  { icon: Eye,        label: 'Readability',   color: 'text-rose-400',    bg: 'bg-rose-500/10' },
  terminology:  { icon: Tag,        label: 'Terminology',   color: 'text-lime-400',    bg: 'bg-lime-500/10' },
  rewrite:      { icon: Wand2,      label: 'Rewrite',       color: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10' },
};

const SEVERITY_CONFIG = {
  critical:      { color: 'text-red-400',    bg: 'bg-red-500/15',    border: 'border-red-500/40' },
  moderate:      { color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/40' },
  minor:         { color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/40' },
  informational: { color: 'text-slate-400',  bg: 'bg-slate-500/15',  border: 'border-slate-500/40' },
};

const CLASS_LABELS = {
  safe_auto_fix: 'Auto-fixed',
  likely_auto_fix: 'Likely Fix',
  user_review_required: 'Needs Review',
  informational_only: 'Info',
};

const GENRE_OPTIONS = [
  { key: 'commercial_fiction', label: 'Commercial Fiction' },
  { key: 'literary', label: 'Literary Fiction' },
  { key: 'thriller', label: 'Thriller' },
  { key: 'romance', label: 'Romance' },
  { key: 'fantasy', label: 'Fantasy' },
  { key: 'scifi', label: 'Sci-Fi' },
  { key: 'christian_fiction', label: 'Christian Fiction' },
  { key: 'memoir', label: 'Memoir' },
  { key: 'custom', label: 'Custom' },
];

const PIPELINE_PASSES = [
  { key: 'ingestion', label: 'Ingestion' },
  { key: 'normalization', label: 'Normalization' },
  { key: 'parsing', label: 'Structural Parsing' },
  { key: 'extracting_memory', label: 'Story Memory' },
  { key: 'voice_analysis', label: 'Voice Profile' },
  { key: 'glossary', label: 'Glossary' },
  { key: 'scene_mapping', label: 'Scene Mapping' },
  { key: 'character_arcs', label: 'Character Arcs' },
  { key: 'pacing_analysis', label: 'Pacing Analysis' },
  { key: 'event_memory', label: 'Event Memory' },
  { key: 'detecting', label: 'Issue Detection' },
  { key: 'continuity_check', label: 'Deep Continuity' },
  { key: 'narrative_analysis', label: 'Narrative Analysis' },
  { key: 'rewrite_analysis', label: 'Rewrite Engine' },
  { key: 'scoring', label: 'Chapter Scoring' },
  { key: 'classifying', label: 'Classification' },
  { key: 'patching', label: 'Auto-patching' },
  { key: 'validating', label: 'Validation' },
];

const LocalEditorModal = ({ open, onOpenChange, project, user, onProjectUpdate, onOpenExport }) => {
  const [step, setStep] = useState('start');
  const [jobId, setJobId] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [memory, setMemory] = useState(null);
  const [filter, setFilter] = useState({ status: 'pending', category: null });
  const [bulkApplying, setBulkApplying] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [customText, setCustomText] = useState('');
  const [mode, setMode] = useState('clean');
  const [sensitivity, setSensitivity] = useState('balanced');
  const [genre, setGenre] = useState('commercial_fiction');
  const [report, setReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [activeTab, setActiveTab] = useState('issues');
  const [voiceProfile, setVoiceProfile] = useState(null);
  const [glossary, setGlossary] = useState(null);
  const [sceneMap, setSceneMap] = useState(null);
  const [characterArcs, setCharacterArcs] = useState(null);
  const [pacingData, setPacingData] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [chapterScores, setChapterScores] = useState(null);
  const [eventMemory, setEventMemory] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (open) {
      setStep('start');
      setJobId(null);
      setJobData(null);
      setIssues([]);
      setSummary(null);
      setMemory(null);
      setReport(null);
      setShowReport(false);
      setFilter({ status: 'pending', category: null });
      setActiveTab('issues');
      setVoiceProfile(null);
      setGlossary(null);
      setSceneMap(null);
      setCharacterArcs(null);
      setPacingData(null);
      setChapterScores(null);
      setEventMemory(null);
    }
  }, [open]);

  const startPipeline = async () => {
    setStep('running');
    try {
      const res = await axios.post(`${API}/local-editor/start`, {
        project_id: project.id,
        user_id: user.id,
        mode, sensitivity, genre,
      });
      const jid = res.data.job_id;
      setJobId(jid);

      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/local-editor/status/${jid}`);
          setJobData(s.data);
          if (s.data.status === 'complete') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            await loadResults();
            setStep('results');
            toast.success(`Editorial pass complete! ${s.data.auto_fixed} issues auto-fixed.`);
          } else if (s.data.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            toast.error('Pipeline failed: ' + s.data.progress_message);
            setStep('start');
          }
        } catch (e) { console.error('Poll error:', e); }
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
      setStep('start');
    }
  };

  const loadResults = async () => {
    try {
      const [issueRes, memRes, reportRes, voiceRes, glossRes, sceneRes, arcRes, pacingRes, logRes, scoresRes, eventsRes] = await Promise.all([
        axios.get(`${API}/local-editor/issues/${project.id}?limit=500`),
        axios.get(`${API}/local-editor/memory/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/report/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/voice-profile/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/glossary/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/scenes/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/arcs/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/pacing/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/activity-log/${project.id}?limit=20`).catch(() => null),
        axios.get(`${API}/local-editor/scores/${project.id}`).catch(() => null),
        axios.get(`${API}/local-editor/events/${project.id}`).catch(() => null),
      ]);
      setIssues(issueRes.data.issues || []);
      setSummary(issueRes.data.summary || null);
      if (memRes) setMemory(memRes.data);
      if (reportRes) setReport(reportRes.data);
      if (voiceRes) setVoiceProfile(voiceRes.data?.profile || null);
      if (glossRes) setGlossary(glossRes.data?.entries || null);
      if (sceneRes) setSceneMap(sceneRes.data?.scenes || null);
      if (arcRes) setCharacterArcs(arcRes.data?.arcs || null);
      if (pacingRes) setPacingData(pacingRes.data?.chapters || null);
      if (logRes) setActivityLog(logRes.data?.log || []);
      if (scoresRes) setChapterScores(scoresRes.data?.chapters || null);
      if (eventsRes) setEventMemory(eventsRes.data || null);
    } catch (e) { console.error('Load results error:', e); }
  };

  const resolveIssue = async (issueId, action, custom = null) => {
    try {
      const body = { issue_id: issueId, action };
      if (custom) body.custom_text = custom;
      const res = await axios.post(`${API}/local-editor/resolve-issue`, body);
      if (res.data.success) {
        toast.success(action === 'approve' ? 'Fix applied' : action === 'reject' ? 'Issue dismissed' : 'Saved');
        setIssues(prev => prev.map(i => i.issue_id === issueId ? { ...i, status: action === 'approve' ? 'user_approved' : action === 'reject' ? 'user_rejected' : 'ignored' } : i));
        if (action === 'approve' && onProjectUpdate) onProjectUpdate();
      } else {
        toast.error(res.data.message || 'Action failed');
      }
    } catch (e) {
      toast.error('Failed to resolve issue');
    }
    setExpandedIssue(null);
  };

  const bulkApply = async (action) => {
    setBulkApplying(true);
    try {
      const res = await axios.post(`${API}/local-editor/bulk-action`, { project_id: project.id, action });
      const data = res.data;
      const totalResolved = data.fixed_count || 0;
      const applied = data.applied || 0;
      const alreadyResolved = data.already_resolved || 0;
      if (totalResolved > 0) {
        let msg = `${applied} fixes applied to your manuscript`;
        if (alreadyResolved > 0) msg += `, ${alreadyResolved} already resolved`;
        toast.success(msg + '!', { duration: 5000 });
      } else {
        toast.info(data.message || 'No fixable issues found');
      }
      await loadResults();
      if (totalResolved > 0 && onProjectUpdate) onProjectUpdate();
    } catch (e) { toast.error('Bulk action failed'); }
    setBulkApplying(false);
  };

  const resetAll = async () => {
    if (!window.confirm('Delete all editorial data and start fresh?')) return;
    try {
      await axios.delete(`${API}/local-editor/reset/${project.id}`);
      toast.success('Editorial data cleared');
      setStep('start');
      setIssues([]); setSummary(null); setMemory(null); setJobData(null);
      setVoiceProfile(null); setGlossary(null); setSceneMap(null);
      setCharacterArcs(null); setPacingData(null);
    } catch (e) { toast.error('Reset failed'); }
  };

  const filteredIssues = issues.filter(i => {
    if (filter.status && i.status !== filter.status) return false;
    if (filter.category && i.category !== filter.category) return false;
    return true;
  });

  const pendingCount = issues.filter(i => i.status === 'pending').length;
  const reviewCount = issues.filter(i => i.status === 'pending' && i.classification === 'user_review_required').length;
  const autoFixableCount = issues.filter(i => i.status === 'pending' && (i.classification === 'safe_auto_fix' || i.classification === 'likely_auto_fix') && i.suggested_fix).length;
  const allFixableCount = issues.filter(i => i.status === 'pending' && i.suggested_fix).length;
  const informationalPending = issues.filter(i => i.status === 'pending' && !i.suggested_fix).length;
  const fixedCount = issues.filter(i => i.status === 'auto_fixed' || i.status === 'user_approved').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl bg-slate-900 border-slate-700 text-white" data-testid="local-editor-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Final Polish (Pro)
            <Badge variant="outline" className="text-emerald-400 border-emerald-500/50 text-xs ml-2">LOCAL</Badge>
            <div className="ml-auto"><TutorialTrigger onClick={() => setShowTutorial(true)} /></div>
          </DialogTitle>
        </DialogHeader>

        {/* ===== START SCREEN ===== */}
        {step === 'start' && (
          <div className="space-y-5 py-3">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center border border-emerald-500/20">
                <ShieldCheck className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Professional Manuscript Editor</h3>
              <p className="text-xs text-slate-400 max-w-2xl">
                Zero API calls. Detects spelling, punctuation, repetition, name/location/timeline drift,
                voice analysis, glossary, scene mapping, character arcs, pacing, and more.
              </p>
            </div>

            <Card className="bg-slate-800/80 border-slate-600/50 max-w-2xl backdrop-blur-sm">
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Chapters</span>
                  <span className="text-white font-medium">{project?.chapter_outline?.length || '?'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Words</span>
                  <span className="text-white font-medium">{project?.word_count?.toLocaleString() || '?'}</span>
                </div>

                {/* Genre selector */}
                <div className="pt-2 border-t border-slate-700/60">
                  <p className="text-[10px] text-slate-500 mb-1.5 uppercase tracking-wider">Genre Profile</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {GENRE_OPTIONS.map(g => (
                      <button key={g.key} onClick={() => setGenre(g.key)} data-testid={`genre-${g.key}-btn`}
                        className={`py-1.5 px-3 rounded-md text-[10px] font-medium transition-all border ${genre === g.key ? 'bg-cyan-600 text-white border-cyan-500 shadow-lg shadow-cyan-500/20' : 'bg-slate-800 text-slate-400 border-slate-600/50 hover:bg-slate-700 hover:text-slate-300 hover:border-slate-500'}`}>
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mode selector */}
                <div className="pt-2 border-t border-slate-700/60">
                  <p className="text-[10px] text-slate-500 mb-1.5 uppercase tracking-wider">Editing Mode</p>
                  <div className="flex gap-2">
                    <button onClick={() => setMode('clean')} data-testid="mode-clean-btn"
                      className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-medium transition-all border ${mode === 'clean' ? 'bg-emerald-600 text-white border-emerald-500 shadow-lg shadow-emerald-500/20' : 'bg-slate-800 text-slate-400 border-slate-600/50 hover:bg-slate-700 hover:border-slate-500'}`}>
                      Clean & Consistent
                      <span className={`block text-[9px] mt-0.5 ${mode === 'clean' ? 'text-emerald-200' : 'text-slate-500'}`}>Spelling, grammar, continuity</span>
                    </button>
                    <button onClick={() => setMode('polish')} data-testid="mode-polish-btn"
                      className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-medium transition-all border ${mode === 'polish' ? 'bg-cyan-600 text-white border-cyan-500 shadow-lg shadow-cyan-500/20' : 'bg-slate-800 text-slate-400 border-slate-600/50 hover:bg-slate-700 hover:border-slate-500'}`}>
                      Full Polish
                      <span className={`block text-[9px] mt-0.5 ${mode === 'polish' ? 'text-cyan-200' : 'text-slate-500'}`}>+ voice, pacing, readability, arcs</span>
                    </button>
                  </div>
                </div>

                {/* Sensitivity selector */}
                <div className="pt-2 border-t border-slate-700/60">
                  <p className="text-[10px] text-slate-500 mb-1.5 uppercase tracking-wider">Voice Sensitivity</p>
                  <div className="flex gap-1.5">
                    {[
                      { key: 'conservative', label: 'Conservative', desc: 'Only obvious issues' },
                      { key: 'balanced', label: 'Balanced', desc: 'Fix + improve' },
                      { key: 'aggressive', label: 'Aggressive', desc: 'Deep tightening' },
                    ].map(s => (
                      <button key={s.key} onClick={() => setSensitivity(s.key)} data-testid={`sensitivity-${s.key}-btn`}
                        className={`flex-1 py-2 px-2 rounded-md text-[10px] font-medium transition-all border ${sensitivity === s.key ? 'bg-teal-600 text-white border-teal-500 shadow-lg shadow-teal-500/20' : 'bg-slate-800 text-slate-400 border-slate-600/50 hover:bg-slate-700 hover:text-slate-300 hover:border-slate-500'}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-emerald-400/80 pt-2 border-t border-slate-700/60">
                  No credits used. Analysis runs entirely on the server with deterministic rules.
                </p>
              </CardContent>
            </Card>

            <div className="flex justify-center">
              <Button onClick={startPipeline} className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white px-10 py-3 text-sm font-bold shadow-lg shadow-emerald-500/25 border-0" data-testid="start-editorial-btn">
                <Play className="w-4 h-4 mr-2" /> Run Editorial Pass
              </Button>
            </div>
          </div>
        )}

        {/* ===== RUNNING ===== */}
        {step === 'running' && (
          <div className="space-y-5 py-5">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-emerald-400 mb-3 animate-spin" />
              <h3 className="text-lg font-bold text-white mb-1">
                {jobData?.current_pass ? `Pass: ${jobData.current_pass.replace('_', ' ')}` : 'Starting...'}
              </h3>
              <p className="text-sm text-slate-400">{jobData?.progress_message || 'Initializing pipeline...'}</p>
            </div>

            <div className="max-w-2xl space-y-2">
              <div className="flex justify-between text-xs text-slate-400">
                <span>Progress</span>
                <span>{jobData?.progress || 0}%</span>
              </div>
              <Progress value={jobData?.progress || 0} className="h-2" />
            </div>

            <div className="sm:max-w-lg grid grid-cols-2 gap-1.5 text-xs">
              {PIPELINE_PASSES.map(p => {
                const current = jobData?.current_pass;
                const passKeys = PIPELINE_PASSES.map(pp => pp.key);
                const currentIdx = passKeys.indexOf(current);
                const thisIdx = passKeys.indexOf(p.key);
                const done = currentIdx > thisIdx || jobData?.status === 'complete';
                const active = current === p.key;
                return (
                  <div key={p.key} className={`flex items-center gap-1.5 ${done ? 'text-emerald-400' : active ? 'text-white' : 'text-slate-600'}`}>
                    {done ? <CheckCircle2 className="w-3 h-3" /> : active ? <Loader2 className="w-3 h-3 animate-spin" /> : <div className="w-3 h-3 rounded-full border border-slate-600" />}
                    {p.label}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300 text-xs">
                Close (continues in background)
              </Button>
            </div>
          </div>
        )}

        {/* ===== RESULTS ===== */}
        {step === 'results' && (
          <div className="space-y-3">
            {/* Score cards */}
            <div className="grid grid-cols-5 gap-2">
              {[
                { label: 'Spelling', score: jobData?.spelling_score, tip: 'Spelling accuracy across your manuscript' },
                { label: 'Punctuation', score: jobData?.punctuation_score, tip: 'Punctuation and dialogue formatting' },
                { label: 'Continuity', score: jobData?.continuity_score, tip: 'Character names, places, and timeline consistency' },
                { label: 'Formatting', score: jobData?.formatting_score, tip: 'Paragraph structure and whitespace formatting' },
                { label: 'Overall', score: jobData?.overall_score, tip: 'Combined editorial quality score' },
              ].map(s => (
                <Card key={s.label} className="bg-slate-800/50 border-slate-700" title={s.tip}>
                  <CardContent className="p-2 text-center">
                    <p className={`text-lg font-bold ${(s.score ?? 0) >= 80 ? 'text-emerald-400' : (s.score ?? 0) >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {s.score ?? '--'}
                    </p>
                    <p className="text-[9px] text-slate-400">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Summary stats */}
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <Badge className="bg-emerald-500/20 text-emerald-300" title="Issues the engine already fixed automatically">{jobData?.auto_fixed || 0} auto-fixed</Badge>
              <Badge className="bg-amber-500/20 text-amber-300" title="Issues that need your review or can be auto-fixed">{reviewCount} need review</Badge>
              <Badge className="bg-slate-500/20 text-slate-300" title="Notes and observations — no fix needed">{jobData?.informational || 0} informational</Badge>
              {allFixableCount > 0 && <Badge className="bg-cyan-500/20 text-cyan-300" title="Total issues that have a suggested fix you can apply">{allFixableCount} fixable</Badge>}
            </div>

            {/* Tab navigation */}
            <div className="flex gap-1 border-b border-slate-700 pb-0.5 overflow-x-auto">
              {[
                { key: 'issues', label: 'Issues', icon: AlertTriangle, count: pendingCount, tip: 'Spelling, grammar, continuity issues found in your manuscript' },
                { key: 'voice', label: 'Voice', icon: Mic, tip: 'Your writing voice fingerprint — sentence length, dialogue ratio, signature phrases' },
                { key: 'glossary', label: 'Glossary', icon: BookText, count: glossary?.length, tip: 'Auto-detected character names, places, and key terms' },
                { key: 'scenes', label: 'Scenes', icon: Compass, count: sceneMap?.length, tip: 'Scene-by-scene map — POV, location, time, characters' },
                { key: 'arcs', label: 'Arcs', icon: TrendingUp, count: characterArcs ? Object.keys(characterArcs).length : 0, tip: 'Character development and emotional journey tracking' },
                { key: 'pacing', label: 'Pacing', icon: Zap, count: pacingData?.length, tip: 'Chapter pacing — dialogue vs action vs introspection' },
                { key: 'scores', label: 'Scores', icon: ShieldCheck, count: chapterScores?.length, tip: 'Per-chapter quality scores — tension, craft, sensory balance' },
                { key: 'report', label: 'Report', icon: FileText, tip: 'Full editorial summary and manuscript readiness assessment' },
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
                    title={tab.tip}
                    className={`flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium rounded-t transition-all whitespace-nowrap ${activeTab === tab.key ? 'bg-slate-800 text-white border-b-2 border-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>
                    <Icon className="w-3 h-3" />
                    {tab.label}
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className="text-[9px] bg-slate-700 px-1 rounded">{tab.count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ===== ISSUES TAB ===== */}
            {activeTab === 'issues' && (
              <div className="space-y-2">
                {/* ===== PROMINENT AUTO-FIX BANNER ===== */}
                {allFixableCount > 0 && (
                  <Card className="bg-emerald-500/10 border-emerald-500/30" data-testid="auto-fix-banner">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-emerald-300 font-semibold text-sm">
                            {allFixableCount} issues can be auto-fixed
                          </p>
                          <p className="text-emerald-400/70 text-xs mt-0.5">
                            One click applies all suggested fixes directly to your manuscript.
                          </p>
                        </div>
                        <Button
                          size="lg"
                          onClick={() => bulkApply('apply_all_fixable')}
                          disabled={bulkApplying}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 py-3 text-sm shrink-0"
                          data-testid="auto-fix-all-btn"
                        >
                          {bulkApplying ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fixing...</>
                          ) : (
                            <><Wand2 className="w-4 h-4 mr-2" /> Auto-Fix All ({allFixableCount})</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Dismiss informational banner — shows when fixable issues are done but info issues remain */}
                {allFixableCount === 0 && informationalPending > 0 && (
                  <Card className="bg-amber-500/10 border-amber-500/30" data-testid="dismiss-info-banner">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-amber-300 font-semibold text-sm">
                            {informationalPending} informational issues remaining
                          </p>
                          <p className="text-amber-400/70 text-xs mt-0.5">
                            These are observations with no auto-fix. Dismiss them to clear your queue.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => bulkApply('dismiss_informational')}
                          disabled={bulkApplying}
                          className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-4 py-2 text-xs shrink-0"
                          data-testid="dismiss-info-btn"
                        >
                          {bulkApplying ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Dismissing...</>
                          ) : (
                            <><X className="w-3 h-3 mr-1" /> Dismiss All Informational</>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Fixed confirmation banner */}
                {fixedCount > 0 && pendingCount === 0 && (
                  <Card className="bg-emerald-500/10 border-emerald-500/30">
                    <CardContent className="p-3 text-center">
                      <CheckCircle2 className="w-7 h-7 text-emerald-400 mb-1" />
                      <p className="text-emerald-300 font-semibold text-sm">All Fixes Applied to Your Manuscript</p>
                      <p className="text-emerald-400/70 text-xs mt-0.5">{fixedCount} issues fixed. Changes saved to your project.</p>
                    </CardContent>
                  </Card>
                )}

                {/* Bulk action buttons with labels */}
                <div className="flex items-center gap-2 flex-wrap">
                  {autoFixableCount > 0 && (
                    <Button size="sm" onClick={() => bulkApply('apply_safe')} disabled={bulkApplying}
                      className="bg-cyan-600 hover:bg-cyan-500 text-xs h-7" data-testid="bulk-safe-fix-btn"
                      title="Apply only safe, low-risk fixes (spelling, punctuation)">
                      {bulkApplying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                      Safe Fixes Only
                    </Button>
                  )}
                  {informationalPending > 0 && allFixableCount === 0 && (
                    <Button size="sm" onClick={() => bulkApply('dismiss_all_pending')} disabled={bulkApplying}
                      variant="outline" className="border-slate-600 text-xs h-7 text-slate-400"
                      data-testid="dismiss-all-pending-btn"
                      title="Dismiss all remaining pending issues (both fixable and informational)">
                      <X className="w-3 h-3 mr-1" /> Dismiss All Pending
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button size="sm" variant="ghost" onClick={resetAll}
                    className="text-red-400 hover:text-red-300 text-xs h-7" data-testid="reset-editorial-btn"
                    title="Delete all editorial data and start a fresh scan">
                    <Trash2 className="w-3 h-3 mr-1" /> Reset
                  </Button>
                  <Button size="sm" variant="ghost" onClick={startPipeline}
                    className="text-slate-400 text-xs h-7"
                    title="Re-run the full editorial scan on your manuscript">
                    <RefreshCw className="w-3 h-3 mr-1" /> Re-scan
                  </Button>
                </div>

                {/* Filter bar with labels */}
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] text-slate-500 mr-1" title="Filter issues by status">Filter:</span>
                  {[
                    { key: 'pending', label: 'pending', desc: 'Issues waiting for action' },
                    { key: 'auto_fixed', label: 'auto fixed', desc: 'Already fixed by the engine' },
                    { key: 'user_approved', label: 'user approved', desc: 'You approved these fixes' },
                    { key: 'user_rejected', label: 'user rejected', desc: 'You dismissed these issues' },
                    { key: 'ignored', label: 'ignored', desc: 'Globally ignored patterns' },
                  ].map(s => (
                    <Button key={s.key} size="sm" variant={filter.status === s.key ? 'default' : 'ghost'}
                      onClick={() => setFilter(f => ({ ...f, status: f.status === s.key ? null : s.key }))}
                      title={s.desc}
                      className={`text-[10px] h-6 px-2 ${filter.status === s.key ? 'bg-slate-700' : 'text-slate-500'}`}>
                      {s.label} ({issues.filter(i => i.status === s.key).length})
                    </Button>
                  ))}
                  <div className="w-px h-4 bg-slate-700 mx-0.5" />
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                    const count = issues.filter(i => i.category === key && (!filter.status || i.status === filter.status)).length;
                    if (!count) return null;
                    const Icon = cfg.icon;
                    return (
                      <Button key={key} size="sm" variant={filter.category === key ? 'default' : 'ghost'}
                        onClick={() => setFilter(f => ({ ...f, category: f.category === key ? null : key }))}
                        title={`Filter by ${cfg.label} issues`}
                        className={`text-[10px] h-6 px-2 ${filter.category === key ? 'bg-slate-700' : 'text-slate-500'}`}>
                        <Icon className="w-3 h-3 mr-0.5" />{cfg.label} {count}
                      </Button>
                    );
                  })}
                </div>

                {/* Issues list */}
                <div className="max-h-[500px] overflow-y-auto space-y-1.5 pr-1" data-testid="issues-list">
                  {filteredIssues.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-2" />
                      <p className="text-white font-medium text-sm">All Clear</p>
                      <p className="text-xs text-slate-400">No issues matching this filter</p>
                    </div>
                  ) : (
                    filteredIssues.map(issue => (
                      <IssueRow key={issue.issue_id} issue={issue}
                        expanded={expandedIssue === issue.issue_id}
                        onToggle={() => setExpandedIssue(expandedIssue === issue.issue_id ? null : issue.issue_id)}
                        onResolve={resolveIssue} customText={customText} setCustomText={setCustomText}
                        projectId={project.id} userId={user.id} onRefresh={loadResults} />
                    ))
                  )}
                </div>

                {pendingCount === 0 && fixedCount === 0 && (
                  <Card className="bg-emerald-500/10 border-emerald-500/30">
                    <CardContent className="p-3 text-center">
                      <CheckCircle2 className="w-7 h-7 text-emerald-400 mb-1" />
                      <p className="text-emerald-300 font-semibold text-sm">Editorially Clean</p>
                      <p className="text-emerald-400/70 text-xs mt-0.5">No detected issues remain.</p>
                    </CardContent>
                  </Card>
                )}

                {/* Export prompt when all pending issues are resolved */}
                {pendingCount === 0 && issues.length > 0 && onOpenExport && (
                  <Card className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border-cyan-500/30 mt-2">
                    <CardContent className="p-3 flex items-center gap-3">
                      <BookOpen className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">Ready to export</p>
                        <p className="text-slate-400 text-xs">Your manuscript is clean. Generate a PDF or ePub with title page, TOC, and professional formatting.</p>
                      </div>
                      <Button size="sm"
                        className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white flex-shrink-0"
                        data-testid="export-from-editor-btn"
                        onClick={() => { onOpenChange(false); onOpenExport(true); }}>
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Export
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* ===== ACTIVITY LOG ===== */}
                {activityLog.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50" data-testid="activity-log">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Activity Log
                    </p>
                    <div className="max-h-[200px] overflow-y-auto space-y-0.5 pr-1">
                      {activityLog.map((entry, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-[10px] py-0.5">
                          <span className={`shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full ${entry.type === 'bulk' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                          <span className="text-slate-400 shrink-0 w-14">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                          <span className="text-slate-300 truncate">{entry.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== VOICE TAB ===== */}
            {activeTab === 'voice' && <VoiceTab profile={voiceProfile} />}

            {/* ===== GLOSSARY TAB ===== */}
            {activeTab === 'glossary' && <GlossaryTab entries={glossary} projectId={project.id} />}

            {/* ===== SCENES TAB ===== */}
            {activeTab === 'scenes' && <ScenesTab scenes={sceneMap} />}

            {/* ===== ARCS TAB ===== */}
            {activeTab === 'arcs' && <ArcsTab arcs={characterArcs} />}

            {/* ===== PACING TAB ===== */}
            {activeTab === 'pacing' && <PacingTab chapters={pacingData} />}

            {/* ===== SCORES TAB ===== */}
            {activeTab === 'scores' && <ScoresTab chapters={chapterScores} />}

            {/* ===== REPORT TAB ===== */}
            {activeTab === 'report' && <ReportTab report={report} />}
          </div>
        )}

        {/* Interactive Tutorial */}
        <EditorTutorial isVisible={showTutorial} onClose={() => setShowTutorial(false)} />
      </DialogContent>
    </Dialog>
  );
};


// ===== VOICE TAB =====
const VoiceTab = ({ profile }) => {
  if (!profile) return <EmptyState label="Voice profile" />;
  return (
    <div className="space-y-3 max-h-[550px] overflow-y-auto pr-1" data-testid="voice-tab">
      <p className="text-xs text-slate-400">Your manuscript's voice fingerprint. The engine uses this to protect your natural writing style.</p>
      <div className="grid grid-cols-2 gap-3">
        <MetricCard label="Avg Sentence Length" value={`${profile.avg_sentence_length} words`} />
        <MetricCard label="Sentence Variety (std)" value={profile.sentence_length_std} />
        <MetricCard label="Short Sentence Ratio" value={`${(profile.short_sentence_ratio * 100).toFixed(1)}%`} />
        <MetricCard label="Long Sentence Ratio" value={`${(profile.long_sentence_ratio * 100).toFixed(1)}%`} />
        <MetricCard label="Fragment Ratio" value={`${(profile.fragment_ratio * 100).toFixed(1)}%`} />
        <MetricCard label="Dialogue Ratio" value={`${(profile.dialogue_ratio * 100).toFixed(1)}%`} />
        <MetricCard label="Avg Para Sentences" value={profile.paragraph_avg_sentences} />
        <MetricCard label="Adverb Density" value={`${(profile.adverb_density * 100).toFixed(2)}%`} />
        <MetricCard label="Filter Word Density" value={`${(profile.filter_word_density * 100).toFixed(2)}%`} />
        <MetricCard label="Exclamation Density" value={`${(profile.exclamation_density * 100).toFixed(2)}%`} />
        <MetricCard label="Em-dash Density" value={`${(profile.em_dash_density * 100).toFixed(2)}%`} />
        <MetricCard label="Ellipsis Density" value={`${(profile.ellipsis_density * 100).toFixed(2)}%`} />
      </div>
      {profile.signature_phrases?.length > 0 && (
        <div>
          <p className="text-xs text-slate-300 font-medium mb-1">Signature Phrases</p>
          <div className="flex flex-wrap gap-1">
            {profile.signature_phrases.map((p, i) => (
              <Badge key={i} className="bg-violet-500/15 text-violet-300 text-[10px]">{p}</Badge>
            ))}
          </div>
        </div>
      )}
      {profile.top_sentence_starters && Object.keys(profile.top_sentence_starters).length > 0 && (
        <div>
          <p className="text-xs text-slate-300 font-medium mb-1">Top Sentence Starters</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(profile.top_sentence_starters).slice(0, 10).map(([starter, count]) => (
              <Badge key={starter} className="bg-slate-700 text-slate-300 text-[10px]">"{starter}" ({count})</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ===== GLOSSARY TAB =====
const GlossaryTab = ({ entries, projectId }) => {
  if (!entries || entries.length === 0) return <EmptyState label="Glossary" />;

  const lockEntry = async (entryId, locked) => {
    try {
      await axios.post(`${API}/local-editor/glossary/lock`, { project_id: projectId, entry_id: entryId, locked });
      toast.success(locked ? 'Entry locked' : 'Entry unlocked');
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-2 max-h-[550px] overflow-y-auto pr-1" data-testid="glossary-tab">
      <p className="text-xs text-slate-400">Auto-detected terms, names, and locations. Lock entries to enforce canonical spelling.</p>
      <div className="space-y-1">
        {entries.map((e, i) => (
          <div key={e.id || i} className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-2 text-xs">
            <Badge className={`text-[10px] border-0 ${e.category === 'character' ? 'bg-violet-500/15 text-violet-300' : e.category === 'place' ? 'bg-emerald-500/15 text-emerald-300' : e.category === 'religious' ? 'bg-amber-500/15 text-amber-300' : 'bg-slate-700 text-slate-300'}`}>
              {e.category}
            </Badge>
            <span className="text-white font-medium">{e.canonical}</span>
            {e.variants?.length > 0 && (
              <span className="text-slate-500">({e.variants.join(', ')})</span>
            )}
            <span className="text-slate-600 ml-auto">{e.occurrences}x</span>
            <button onClick={() => lockEntry(e.id, !e.locked)}
              className={`text-[10px] px-1.5 py-0.5 rounded ${e.locked ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700 text-slate-500 hover:text-slate-300'}`}>
              {e.locked ? 'Locked' : 'Lock'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};


// ===== SCENES TAB =====
const ScenesTab = ({ scenes }) => {
  const [expanded, setExpanded] = useState(null);
  if (!scenes || scenes.length === 0) return <EmptyState label="Scene map" />;
  return (
    <div className="space-y-1.5 max-h-[550px] overflow-y-auto pr-1" data-testid="scenes-tab">
      <p className="text-xs text-slate-400 mb-2">Scene-by-scene breakdown. POV, location, time, characters, and emotional tone.</p>
      {scenes.map((s, i) => (
        <div key={s.scene_id || i} className="bg-slate-800/50 rounded-lg border border-slate-700/50">
          <button onClick={() => setExpanded(expanded === i ? null : i)}
            className="w-full flex items-center gap-2 p-2 text-left text-xs">
            <span className="text-slate-500 w-16 shrink-0">Ch {s.chapter_number}.{s.scene_index + 1}</span>
            <span className="text-white font-medium truncate flex-1">{s.pov_character || 'Unknown POV'}</span>
            {s.location && <Badge className="bg-emerald-500/15 text-emerald-300 text-[10px] border-0">{s.location}</Badge>}
            {s.time_cue && <Badge className="bg-orange-500/15 text-orange-300 text-[10px] border-0">{s.time_cue}</Badge>}
            <Badge className={`text-[10px] border-0 ${s.emotional_tone === 'tense' ? 'bg-red-500/15 text-red-300' : s.emotional_tone === 'romantic' ? 'bg-pink-500/15 text-pink-300' : s.emotional_tone === 'sad' ? 'bg-blue-500/15 text-blue-300' : 'bg-slate-700 text-slate-300'}`}>
              {s.emotional_tone}
            </Badge>
            <span className="text-slate-600 text-[10px]">{s.word_count}w</span>
            {expanded === i ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
          </button>
          {expanded === i && (
            <div className="px-3 pb-2 space-y-1 border-t border-slate-700/50 text-[11px]">
              {s.characters_present?.length > 0 && (
                <div className="flex gap-1 flex-wrap pt-1">
                  <span className="text-slate-500">Characters:</span>
                  {s.characters_present.map(c => <Badge key={c} className="bg-violet-500/15 text-violet-300 text-[10px] border-0">{c}</Badge>)}
                </div>
              )}
              {s.entry_condition && <p className="text-slate-400"><span className="text-slate-500">Opens:</span> {s.entry_condition}</p>}
              {s.exit_condition && <p className="text-slate-400"><span className="text-slate-500">Closes:</span> {s.exit_condition}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};


// ===== ARCS TAB =====
const ArcsTab = ({ arcs }) => {
  const [expanded, setExpanded] = useState(null);
  if (!arcs || Object.keys(arcs).length === 0) return <EmptyState label="Character arcs" />;
  return (
    <div className="space-y-1.5 max-h-[550px] overflow-y-auto pr-1" data-testid="arcs-tab">
      <p className="text-xs text-slate-400 mb-2">Character development tracking. Baseline traits, emotional journey, and turning points.</p>
      {Object.entries(arcs).map(([key, arc]) => (
        <div key={key} className="bg-slate-800/50 rounded-lg border border-slate-700/50">
          <button onClick={() => setExpanded(expanded === key ? null : key)}
            className="w-full flex items-center gap-2 p-2 text-left text-xs">
            <Users className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-white font-medium">{arc.character_name}</span>
            {arc.baseline_traits?.length > 0 && (
              <span className="text-slate-500 text-[10px]">{arc.baseline_traits.join(', ')}</span>
            )}
            <span className="ml-auto text-[10px] text-slate-600">{arc.emotional_journey?.length || 0} chapters</span>
            {expanded === key ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
          </button>
          {expanded === key && (
            <div className="px-3 pb-2 space-y-2 border-t border-slate-700/50 text-[11px]">
              {arc.emotional_journey?.length > 0 && (
                <div>
                  <p className="text-slate-400 font-medium mb-1">Emotional Journey</p>
                  <div className="flex gap-1 flex-wrap">
                    {arc.emotional_journey.map((ej, i) => (
                      <span key={i} className="bg-slate-700 px-1.5 py-0.5 rounded text-[10px]">
                        <span className="text-slate-500">Ch{ej.chapter}:</span> <span className="text-white">{ej.emotion}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {arc.turning_points?.length > 0 && (
                <div>
                  <p className="text-slate-400 font-medium mb-1">Turning Points</p>
                  {arc.turning_points.map((tp, i) => (
                    <p key={i} className="text-amber-300 text-[10px]">Ch {tp.chapter}: {tp.type} event</p>
                  ))}
                </div>
              )}
              {arc.evolving_traits?.length > 0 && (
                <div>
                  <p className="text-slate-400 font-medium mb-1">Evolving Traits</p>
                  {arc.evolving_traits.map((et, i) => (
                    <p key={i} className="text-cyan-300 text-[10px]">Ch {et.chapter}: {et.trait} ({et.direction})</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};


// ===== PACING TAB =====
const PacingTab = ({ chapters }) => {
  if (!chapters || chapters.length === 0) return <EmptyState label="Pacing analysis" />;

  const paceColors = {
    fast: 'text-red-400 bg-red-500/15',
    'dialogue-driven': 'text-pink-400 bg-pink-500/15',
    reflective: 'text-blue-400 bg-blue-500/15',
    slow: 'text-slate-400 bg-slate-500/15',
    balanced: 'text-emerald-400 bg-emerald-500/15',
  };

  return (
    <div className="space-y-2 max-h-[550px] overflow-y-auto pr-1" data-testid="pacing-tab">
      <p className="text-xs text-slate-400 mb-2">Chapter-by-chapter pacing breakdown: dialogue, action, introspection, and exposition ratios.</p>

      {/* Pacing ribbon */}
      <div className="flex gap-0.5 mb-3">
        {chapters.map((ch, i) => (
          <div key={i} title={`Ch ${ch.chapter}: ${ch.pace_label}`}
            className={`h-5 rounded-sm flex-1 min-w-[8px] ${paceColors[ch.pace_label]?.split(' ')[1] || 'bg-slate-700'}`} />
        ))}
      </div>

      <div className="space-y-1">
        {chapters.map((ch, i) => (
          <div key={i} className="flex items-center gap-2 bg-slate-800/40 rounded p-2 text-[11px]">
            <span className="text-slate-500 w-10 shrink-0">Ch {ch.chapter}</span>
            <Badge className={`text-[10px] border-0 ${paceColors[ch.pace_label] || 'text-slate-300 bg-slate-700'}`}>
              {ch.pace_label}
            </Badge>
            <span className="text-slate-500">{ch.word_count}w</span>
            <span className="text-slate-600">DLG {(ch.dialogue_ratio * 100).toFixed(0)}%</span>
            <span className="text-slate-600">ACT {ch.action_density}</span>
            <span className="text-slate-600">INT {ch.introspection_density}</span>
            {ch.pacing_signals?.length > 0 && (
              <AlertTriangle className="w-3 h-3 text-amber-400 ml-auto" title={ch.pacing_signals.join('; ')} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


// ===== REPORT TAB =====
const ReportTab = ({ report }) => {
  if (!report) return <EmptyState label="Final report" />;
  return (
    <div className="space-y-3 max-h-[550px] overflow-y-auto pr-1" data-testid="report-tab">
      {/* Overview */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
        <div className="flex justify-between"><span className="text-slate-400">Chapters</span><span className="text-white">{report.chapters_analyzed}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Words</span><span className="text-white">{report.word_count_estimate?.toLocaleString()}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Mode</span><span className="text-white capitalize">{report.mode}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Sensitivity</span><span className="text-white capitalize">{report.sensitivity}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Genre</span><span className="text-white capitalize">{report.genre?.replace(/_/g, ' ')}</span></div>
      </div>

      {/* Scores */}
      <div className="pt-2 border-t border-slate-700">
        <p className="text-slate-400 text-xs mb-2 font-medium">Scores</p>
        <div className="grid grid-cols-5 gap-2 text-center">
          {['spelling', 'punctuation', 'continuity', 'formatting', 'overall'].map(k => (
            <div key={k}>
              <p className={`text-lg font-bold ${(report.scores?.[k] ?? 0) >= 80 ? 'text-emerald-400' : (report.scores?.[k] ?? 0) >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                {report.scores?.[k] ?? '--'}
              </p>
              <p className="text-[9px] text-slate-500 capitalize">{k}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Issue resolution */}
      <div className="pt-2 border-t border-slate-700 text-xs space-y-1">
        <p className="text-slate-400 font-medium mb-1">Issue Resolution</p>
        <div className="flex justify-between"><span className="text-slate-400">Total detected</span><span className="text-white">{report.issues_summary?.total_detected}</span></div>
        <div className="flex justify-between"><span className="text-emerald-400">Auto-fixed</span><span className="text-emerald-300">{report.issues_summary?.auto_fixed}</span></div>
        <div className="flex justify-between"><span className="text-cyan-400">User approved</span><span className="text-cyan-300">{report.issues_summary?.user_approved}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">Dismissed</span><span className="text-slate-300">{report.issues_summary?.user_rejected}</span></div>
        {(report.issues_summary?.pending || 0) > 0 && (
          <div className="flex justify-between"><span className="text-amber-400">Still pending</span><span className="text-amber-300">{report.issues_summary?.pending}</span></div>
        )}
      </div>

      {/* Tier breakdown */}
      <div className="pt-2 border-t border-slate-700">
        <p className="text-slate-400 text-xs mb-2 font-medium">Issue Tiers</p>
        <div className="grid grid-cols-4 gap-2 text-center">
          {[
            { tier: 1, label: 'Readability', color: 'text-blue-400' },
            { tier: 2, label: 'Continuity', color: 'text-violet-400' },
            { tier: 3, label: 'Voice/Style', color: 'text-amber-400' },
            { tier: 4, label: 'Creative', color: 'text-pink-400' },
          ].map(t => (
            <div key={t.tier}>
              <p className={`text-sm font-bold ${t.color}`}>{report.issues_summary?.by_tier?.[t.tier] || 0}</p>
              <p className="text-[9px] text-slate-500">Tier {t.tier}: {t.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Voice summary in report */}
      {report.voice_profile && (
        <div className="pt-2 border-t border-slate-700 text-xs">
          <p className="text-slate-400 font-medium mb-1">Voice Fingerprint</p>
          <div className="grid grid-cols-3 gap-2">
            <span className="text-white">Avg sentence: {report.voice_profile.avg_sentence_length}w</span>
            <span className="text-white">Dialogue: {(report.voice_profile.dialogue_ratio * 100).toFixed(0)}%</span>
            <span className="text-white">Fragments: {(report.voice_profile.fragment_ratio * 100).toFixed(1)}%</span>
          </div>
        </div>
      )}

      {/* Pacing summary */}
      {report.pacing_summary && (
        <div className="pt-2 border-t border-slate-700 text-xs">
          <p className="text-slate-400 font-medium mb-1">Pacing Breakdown</p>
          <div className="flex gap-3 flex-wrap">
            {Object.entries(report.pacing_summary).filter(([k]) => k !== 'total_chapters').map(([label, count]) => (
              <span key={label} className="text-white capitalize">{label.replace('_', '-')}: {count}</span>
            ))}
          </div>
        </div>
      )}

      {/* Readiness */}
      <div className={`p-3 rounded-lg text-center ${report.readiness === 'Editorially Clean' ? 'bg-emerald-500/10' : report.readiness === 'Mechanically Clean' ? 'bg-cyan-500/10' : 'bg-amber-500/10'}`}>
        <p className={`font-semibold text-sm ${report.readiness === 'Editorially Clean' ? 'text-emerald-300' : report.readiness === 'Mechanically Clean' ? 'text-cyan-300' : 'text-amber-300'}`}>
          {report.readiness}
        </p>
        <p className="text-[10px] text-slate-400 mt-1">{report.readiness_detail}</p>
      </div>
    </div>
  );
};


// ===== SCORES TAB =====
const ScoresTab = ({ chapters }) => {
  if (!chapters || chapters.length === 0) return <EmptyState label="Chapter scores" />;

  const scoreColor = (v) => v >= 80 ? 'text-emerald-400' : v >= 60 ? 'text-yellow-400' : v >= 40 ? 'text-orange-400' : 'text-red-400';
  const barColor = (v) => v >= 80 ? 'bg-emerald-500' : v >= 60 ? 'bg-yellow-500' : v >= 40 ? 'bg-orange-500' : 'bg-red-500';
  const tensionColor = (label) => {
    if (label === 'high_tension') return 'text-red-400';
    if (label === 'moderate_tension') return 'text-orange-400';
    if (label === 'low_tension') return 'text-yellow-400';
    return 'text-cyan-400';
  };

  return (
    <div className="space-y-3 max-h-[550px] overflow-y-auto pr-1" data-testid="scores-tab">
      <p className="text-xs text-slate-400">Per-chapter quality scores. Scores are heuristic, not absolute truth.</p>
      {chapters.map(ch => (
        <Card key={ch.chapter_number} className="bg-slate-800/60 border-slate-700/50">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white font-medium">Chapter {ch.chapter_number}</span>
              <span className={`text-lg font-bold ${scoreColor(ch.scores?.overall || 0)}`}>{ch.scores?.overall || 0}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[10px]">
              {['spelling', 'punctuation', 'continuity', 'repetition', 'style'].map(k => (
                <div key={k} className="flex items-center gap-1">
                  <div className="flex-1 bg-slate-700 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-full rounded-full ${barColor(ch.scores?.[k] || 0)}`} style={{ width: `${ch.scores?.[k] || 0}%` }} />
                  </div>
                  <span className={`${scoreColor(ch.scores?.[k] || 0)} w-6 text-right`}>{ch.scores?.[k] || 0}</span>
                  <span className="text-slate-500 capitalize w-16 truncate">{k}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3 text-[10px] border-t border-slate-700/50 pt-2">
              <span className="text-slate-400">{ch.word_count?.toLocaleString()} words</span>
              {ch.tension && (
                <span className={tensionColor(ch.tension.label)}>
                  Tension: {ch.tension.tension_score}/100 ({ch.tension.label?.replace('_', ' ')})
                </span>
              )}
              {ch.sensory && (
                <span className="text-slate-400">
                  Sensory diversity: {((ch.sensory.diversity || 0) * 100).toFixed(0)}%
                </span>
              )}
              {ch.pacing && (
                <span className="text-slate-400">
                  Dialogue: {((ch.pacing.dialogue_ratio || 0) * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};


// ===== SHARED COMPONENTS =====

const MetricCard = ({ label, value }) => (
  <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700/50">
    <p className="text-[10px] text-slate-500">{label}</p>
    <p className="text-sm text-white font-medium">{value}</p>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="text-center py-10">
    <p className="text-slate-500 text-sm">{label} not available yet. Run the editorial pipeline first.</p>
  </div>
);

const IssueRow = ({ issue, expanded, onToggle, onResolve, customText, setCustomText, projectId, userId, onRefresh }) => {
  const cat = CATEGORY_CONFIG[issue.category] || CATEGORY_CONFIG.spelling;
  const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.minor;
  const Icon = cat.icon;
  const isFixed = issue.status !== 'pending';

  const addToDictionary = async () => {
    try {
      const word = issue.source_span?.split(/\s+/)[0] || issue.source_span;
      await axios.post(`${API}/local-editor/add-to-dictionary`, {
        project_id: projectId, user_id: userId, word, category: issue.category,
      });
      toast.success(`Added "${word}" to dictionary`);
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to add to dictionary'); }
  };

  const addToAllowlist = async () => {
    try {
      const phrase = issue.source_span || '';
      await axios.post(`${API}/local-editor/add-to-allowlist`, {
        project_id: projectId, user_id: userId, phrase, category: issue.category,
      });
      toast.success(`Added to style allowlist`);
      if (onRefresh) onRefresh();
    } catch (e) { toast.error('Failed to add to allowlist'); }
  };

  return (
    <div className={`border rounded-lg transition-all ${isFixed ? 'border-slate-800 opacity-60' : sev.border} ${sev.bg}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-2 p-2.5 text-left" data-testid={`issue-row-${issue.issue_id}`}>
        <Icon className={`w-4 h-4 flex-shrink-0 ${cat.color}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white truncate">{issue.message}</p>
          <p className="text-[10px] text-slate-500">Ch {issue.chapter_number} | {issue.reason_label || issue.category} | Tier {issue.tier || '?'} | {CLASS_LABELS[issue.classification] || issue.classification}</p>
        </div>
        <Badge className={`text-[10px] ${sev.bg} ${sev.color} border-0`}>{issue.severity}</Badge>
        {isFixed && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-0">{issue.status.replace(/_/g,' ')}</Badge>}
        {expanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-700/50">
          {issue.source_span && (
            <div className="mt-2">
              <p className="text-[10px] text-slate-500 mb-1">Found text:</p>
              <code className="text-xs text-red-300 bg-red-500/10 px-2 py-1 rounded block break-all">{issue.source_span}</code>
            </div>
          )}
          {issue.suggested_fix && (
            <div>
              <p className="text-[10px] text-slate-500 mb-1">Suggested fix:</p>
              <code className="text-xs text-emerald-300 bg-emerald-500/10 px-2 py-1 rounded block break-all">{issue.suggested_fix}</code>
            </div>
          )}
          {issue.alternate_options?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              <span className="text-[10px] text-slate-500">Alternatives:</span>
              {issue.alternate_options.map((opt, i) => (
                <Badge key={i} className="text-[10px] bg-slate-700 text-slate-300 cursor-pointer hover:bg-slate-600"
                  onClick={() => onResolve(issue.issue_id, 'custom', opt)}>{opt}</Badge>
              ))}
            </div>
          )}
          {!isFixed && (
            <div className="flex items-center gap-2 pt-1">
              {issue.suggested_fix && (
                <Button size="sm" onClick={() => onResolve(issue.issue_id, 'approve')}
                  className="bg-emerald-600 hover:bg-emerald-500 text-xs h-7" data-testid={`approve-${issue.issue_id}`}
                  title="Accept this fix and apply it to your manuscript">
                  <Check className="w-3 h-3 mr-1" /> Apply This Fix
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onResolve(issue.issue_id, 'reject')}
                className="text-slate-400 text-xs h-7" data-testid={`reject-${issue.issue_id}`}
                title="Dismiss this issue — no changes will be made">
                <X className="w-3 h-3 mr-1" /> Dismiss
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onResolve(issue.issue_id, 'ignore_globally')}
                className="text-slate-500 text-xs h-7"
                title="Ignore this pattern everywhere — future scans will skip it">
                <Eye className="w-3 h-3 mr-1" /> Ignore Pattern
              </Button>
              {issue.category === 'spelling' && (
                <Button size="sm" variant="ghost" onClick={addToDictionary}
                  className="text-blue-400 text-xs h-7" data-testid={`dict-${issue.issue_id}`}
                  title="Add this word to your custom dictionary">
                  <BookText className="w-3 h-3 mr-1" /> Dictionary
                </Button>
              )}
              {(issue.category === 'style' || issue.category === 'rewrite' || issue.category === 'repetition') && (
                <Button size="sm" variant="ghost" onClick={addToAllowlist}
                  className="text-fuchsia-400 text-xs h-7" data-testid={`allowlist-${issue.issue_id}`}
                  title="Add to style allowlist — protects this phrase from future flags">
                  <ShieldCheck className="w-3 h-3 mr-1" /> Allowlist
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocalEditorModal;
